import { BadgeColor, BadgeSize } from '@components/Badge.types.ts';
import { Icon } from '@components/Icon.tsx';
import { IconType } from '@src/IconType.ts';
import { classNames } from '@utils/classNames.ts';

const colors: Record<
  BadgeColor,
  { mainClassNames: string; buttonClassNames?: string }
> = {
  [BadgeColor.RED]: {
    mainClassNames: 'bg-[#FEF3F2] text-[#B42318]',
    buttonClassNames: undefined,
  },
  [BadgeColor.GREY]: {
    mainClassNames: 'bg-[#F2F4F7] text-[#344054]',
    buttonClassNames: undefined,
  },
  [BadgeColor.GREEN]: {
    mainClassNames: 'bg-[#EAF9EF] text-[#59BA89]',
    buttonClassNames: undefined,
  },
  [BadgeColor.BLUE]: {
    mainClassNames: 'bg-blue-50 text-blue-500',
    buttonClassNames: undefined,
  },
  [BadgeColor.YELLOW]: {
    mainClassNames: 'bg-yellow-100 text-yellow-900',
    buttonClassNames: undefined,
  },
} as const;

const sizes: Record<BadgeSize, string> = {
  [BadgeSize.SMALL]: 'px-2 py-[3px] text-xs',
  [BadgeSize.MEDIUM]: 'px-2.5 text-sm',
  [BadgeSize.LARGE]: 'px-3 py-1.5 text-sm',
};

export type BadgeProps = {
  readonly label: string;
  readonly size: BadgeSize;
  readonly color: BadgeColor;
  readonly showDot: boolean;
  readonly deletable: boolean;
  readonly onDelete?: () => void;
};

export function Badge({
  label,
  size,
  color,
  showDot,
  deletable,
  onDelete,
}: BadgeProps) {
  return (
    <span
      className={classNames(
        colors[color].mainClassNames,
        sizes[size],
        'inline-flex items-center rounded-lg py-0.5 font-normal',
      )}
    >
      {showDot && (
        <svg
          className="-ml-1 mr-1.5 h-2 w-2"
          fill="currentColor"
          viewBox="0 0 8 8"
        >
          <circle cx={4} cy={4} r={3} />
        </svg>
      )}
      {label}
      {deletable && (
        <button
          type="button"
          className={classNames(
            'ml-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full focus:outline-none',
            colors[color].buttonClassNames ??
              'hover:!bg-white focus:bg-white focus:bg-opacity-60',
          )}
          onClick={onDelete}
        >
          <span className="sr-only"></span>
          <Icon type={IconType.X} />
        </button>
      )}
    </span>
  );
}
